import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { type SEOHandle } from "@nasa-gcn/remix-seo";
import {
  Html,
  Container,
  Text,
  Link as EmailLink,
} from "@react-email/components";
import {
  data,
  redirect,
  type ActionFunctionArgs,
  type MetaFunction,
} from "react-router";
import { useFetcher } from "react-router";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { z } from "zod";
import { GeneralErrorBoundary } from "~/components/error-boundary";
import { ErrorList, Field } from "~/components/forms";
import { StatusButton } from "~/components/ui/status-button";
import { sendEmail } from "~/lib/utils/email.server";
import { checkHoneypot } from "~/lib/utils/honeypot.server";
import { EmailSchema, UsernameSchema } from "~/lib/utils/user-validation";
import { prepareVerification } from "./verify.server";
import { db } from "~/lib/db";
import { Icon } from "~/components/ui/icon";
import { TenantLink } from "~/components/tenant-link";

export const handle: SEOHandle = {
  getSitemapEntries: () => null,
};

const ForgotPasswordSchema = z.object({
  usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();

  checkHoneypot(formData);

  const submission = await parseWithZod(formData, {
    schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
      const user = await db.query.users.findFirst({
        where: (user, { or, eq }) =>
          or(
            eq(user.email, data.usernameOrEmail),
            eq(user.username, data.usernameOrEmail)
          ),
      });

      if (!user) {
        // @overwrite – We don't want to let users know whether the
        // user exists or not in our database
        // Note – Similar functionality in `/routes/_auth+/reset-password.server.ts`
        ctx.addIssue({
          path: ["usernameOrEmail"],
          code: z.ZodIssueCode.custom,
          message: "No user exists with this username or email",
        });
        return;
      }
    }),
    async: true,
  });

  if (submission.status !== "success") {
    return data(
      { result: submission.reply() },
      { status: submission.status === "error" ? 400 : 200 }
    );
  }

  const { usernameOrEmail } = submission.value;

  const user = await db.query.users.findFirst({
    where: (user, { or, eq }) =>
      or(eq(user.email, usernameOrEmail), eq(user.username, usernameOrEmail)),
  });

  if (!user) {
    return data(
      {
        result: submission.reply({
          fieldErrors: {
            usernameOrEmail: ["User with username or email not found"],
          },
        }),
      },
      { status: 400 }
    );
  }

  const { verifyUrl, redirectTo, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: "reset-password",
    target: usernameOrEmail,
  });

  const response = await sendEmail({
    to: user.email,
    subject: "Resetea tu contraseña con Fuel Portfolio",
    react: (
      <ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
    ),
  });

  if (response.status === "success") {
    return redirect(redirectTo.toString());
  } else {
    return data(
      { result: submission.reply({ formErrors: [response.error.message] }) },
      { status: 500 }
    );
  }
}

function ForgotPasswordEmail({
  onboardingUrl,
  otp,
}: {
  onboardingUrl: string;
  otp: string;
}) {
  return (
    <Html lang="es" dir="ltr">
      <Container>
        <h1>
          <Text>Resetea tu contraseña con Fuel Portfolio</Text>
        </h1>
        <p>
          <Text>
            Aquí esta tu codigo de verificación: <strong>{otp}</strong>
          </Text>
        </p>
        <p>
          <Text>O dale click a este link:</Text>
        </p>
        <EmailLink href={onboardingUrl}>{onboardingUrl}</EmailLink>
      </Container>
    </Html>
  );
}

export const meta: MetaFunction = () => {
  return [{ title: "Recupera tu contraseña con Fuel Portfolio" }];
};

export default function ForgotPasswordRoute() {
  const forgotPassword = useFetcher<typeof action>();

  const [form, fields] = useForm({
    id: "forgot-password-form",
    constraint: getZodConstraint(ForgotPasswordSchema),
    lastResult: forgotPassword.data?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ForgotPasswordSchema });
    },
    shouldRevalidate: "onBlur",
  });

  return (
    <div className="flex flex-col items-center justify-center w-full gap-10 h-dvh">
      <div className="flex flex-col gap-3 text-center">
        <h1 className="font-black text-h1">Olvidaste tu contraseña?</h1>
        <p className="text-body-md text-muted-foreground">A todos nos pasa.</p>
      </div>
      <div className="flex flex-col gap-4 justify-center w-full md:max-w-[320px] p-6">
        <forgotPassword.Form
          method="POST"
          {...getFormProps(form)}
          className="flex flex-col gap-4"
        >
          <HoneypotInputs />
          <Field
            labelProps={{
              htmlFor: fields.usernameOrEmail.id,
              children: "Email o nombre de usuario",
            }}
            inputProps={{
              autoFocus: true,
              ...getInputProps(fields.usernameOrEmail, { type: "text" }),
            }}
            errors={fields.usernameOrEmail.errors}
            helperText="Te enviaremos un codigo de confirmación a tu email"
          />

          <ErrorList errors={form.errors} id={form.errorId} />

          <StatusButton
            className="w-full"
            status={
              forgotPassword.state === "submitting"
                ? "pending"
                : form.status ?? "idle"
            }
            type="submit"
            disabled={forgotPassword.state !== "idle"}
          >
            Recupera tu contraseña
          </StatusButton>
        </forgotPassword.Form>
        <TenantLink
          to="/login"
          className="mr-4 text-center underline group text-body-sm"
        >
          <Icon
            name="arrow-left"
            className="transition-all translate-x-4 opacity-0 group-hover:opacity-100 group-hover:-translate-x-2"
          />
          Regresa al login
        </TenantLink>
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
